// src/hooks/useFreeShippingThreshold.js
//
// Obtiene el umbral (monto mínimo) para envío gratis desde el backend, una sola vez,
// cacheándolo en memoria y en localStorage ('free_shipping_threshold') para que
// FloatCart, endcart, checkout y payment lo compartan sin múltiples requests.

import { useState, useEffect } from 'react';

const STORAGE_KEY = 'free_shipping_threshold';

let cached = null; // number | null
let inflight = null;

const readStorage = () => {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(STORAGE_KEY);
  const n = raw ? Number(raw) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
};

const fetchThreshold = () => {
  if (cached != null) return Promise.resolve(cached);
  if (inflight) return inflight;

  inflight = fetch(`${process.env.BASE_URL}/shipping/free-shipping-threshold`, {
    headers: { Accept: 'application/json' },
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      const t =
        data && data.enabled && Number(data.threshold) > 0
          ? Number(data.threshold)
          : null;
      cached = t;
      if (t != null && typeof localStorage !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, String(t));
      }
      return t;
    })
    .catch(() => null)
    .finally(() => {
      inflight = null;
    });

  return inflight;
};

// `disabled` (opcional): si es true, la barra de envío gratis no aplica para este
// usuario (p.ej. tiene no_free_shipping). En ese caso devolvemos null y no hacemos
// el fetch. Sin argumento, el comportamiento es idéntico al original.
export default function useFreeShippingThreshold(disabled = false) {
  const [threshold, setThreshold] = useState(() =>
    disabled ? null : cached ?? readStorage()
  );

  useEffect(() => {
    if (disabled) {
      setThreshold(null);
      return undefined;
    }
    let mounted = true;
    fetchThreshold().then((t) => {
      if (mounted && t != null) setThreshold(t);
    });
    return () => {
      mounted = false;
    };
  }, [disabled]);

  return disabled ? null : threshold;
}
